// Re-export LabelsEnum for convenience
export { LabelsEnum } from "./types/LabelsEnum";

// Constants used across the application
export const baseCommissionRate = 0.15;

// Brand logo as the primary placeholder image
export const placeholderPaths = {
  image: "/brand/tutuly-jewellery-logo.png",
} as const;

export const placeholderImage = {
  id: "placeholder",
  src: placeholderPaths.image,
  thumbnailSrc: placeholderPaths.image,
  alt: "TUTULY jewellery",
} as const;

// Blur data URL for loading placeholder effect
// This is a tiny base64-encoded blurred version of the brand logo
// Generated to provide smooth loading transition before main image loads
export const blurDataURL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAQAAAAECAYAAACp8Z5+AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAA/SURBVHgBbYtBCQAwDAPv0/5bOGiXQzAR8UJEZmZ1V1W1u/t+OOfcnXPOOeecc84BAAAA//8DAF3nB3sPcqyBAAAAAElFTkSuQmCC";

// Base URL for the application (used for Stripe return_url, etc.)
// Set NEXT_PUBLIC_BASE_URL or BASE_URL in .env - no fallbacks in code
export const BaseUrl = process.env.NEXT_PUBLIC_BASE_URL ?? process.env.BASE_URL ?? "";

// Shipping fallbacks — live values come from CMS `shipping-settings` global
export {
  DEFAULT_SHIPPING_METHOD_ID,
  DEFAULT_SHIPPING_SETTINGS,
  FreeShippingStartValue,
  ShippingFee,
} from "../../features/shipping/shared/defaults";

// Affiliate constants
export const affiliateWithdrawalLimit = 10;
export const InfoEmail = "customerservice@tutulyjewellery.com";

// Map legacy/invalid currency codes to Stripe ISO 4217 codes
export const CURRENCY_CODE_MAP: Record<string, string> = {
  ukp: "gbp",
};

export function toStripeCurrency(currencyCode: string): string {
  const normalized = currencyCode.toLowerCase();
  return CURRENCY_CODE_MAP[normalized] ?? normalized;
}

// Default country
export interface ICountry {
  id: string;
  country: string;
  countryCode: string;
  currencyCode: string;
  exchangeRate: number;
  symbol: string;
}

/** Default country when no country is selected - United Kingdom (GBP)
 * Exchange rate is relative to Chinese Yuan (CNY) - how much GBP you get for 1 CNY
 */
export const defaultCountry: ICountry = {
  id: "default-uk",
  country: "United Kingdom",
  countryCode: "GB",
  currencyCode: "GBP",
  exchangeRate: 0.11,
  symbol: "£",
};

// Map ISO 3166-1 (geo APIs) to our DB country codes
export const GEO_TO_COUNTRY_CODE: Record<string, string> = {
  GB: "GB",
  UK: "GB",
  US: "US",
  CN: "CN",
  JP: "JP",
  DE: "DE",
  FR: "FR",
  AU: "AU",
  CA: "CA",
  IN: "IN",
  KR: "KR",
  ES: "ES",
  IT: "IT",
  NL: "NL",
  RU: "RU",
  BR: "BR",
  MX: "MX",
  SG: "SG",
  HK: "HK",
  TW: "TW",
};

// Navigation constants
export const navigationMenuTriggerStyle =
  "group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-base-200 focus:bg-base-200 focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=open]:bg-base-300";

// Navigation items - TODO: Move to Payload CMS or feature-specific config
export interface INavItem {
  name: string;
  href: string;
  subItems?: INavItem[];
}

export const navItems: INavItem[] = [
  { name: "home", href: "/" },
  {
    name: "new_arrival",
    href: "/products/categories/new",
    subItems: [
      { name: "bracelets", href: "/products/categories/new/bracelets" },
      { name: "necklaces", href: "/products/categories/new/necklaces" },
      { name: "earrings", href: "/products/categories/new/earrings" },
      { name: "rings", href: "/products/categories/new/rings" },
    ],
  },
  {
    name: "jewellery",
    href: "/products/jewellery",
    subItems: [
      { name: "bracelets", href: "/products/jewellery/bracelets" },
      { name: "necklaces", href: "/products/jewellery/necklaces" },
      { name: "earrings", href: "/products/jewellery/earrings" },
      { name: "rings", href: "/products/jewellery/rings" },
    ],
  },
  { name: "gift_sets", href: "/gift-sets" },
  {
    name: "sales",
    href: "/products/categories/sales",
    subItems: [
      { name: "bracelets", href: "/products/categories/sales/bracelets" },
      { name: "necklaces", href: "/products/categories/sales/necklaces" },
      { name: "earrings", href: "/products/categories/sales/earrings" },
      { name: "rings", href: "/products/categories/sales/rings" },
    ],
  },
  { name: "blog", href: "/blog" },
  { name: "about_us", href: "/about" },
];

/** Supported storefront + Payload localization. */
export const locales = ["en", "de", "fr", "ja", "zh"] as const;

export type Locale = (typeof locales)[number];

/** Removed storefront locales → permanent redirect target (301). */
export const deprecatedLocaleRedirects: Record<string, Locale> = {
  es: "en",
  it: "en",
  fi: "en",
  ko: "en",
  ru: "en",
  sv: "en",
  nl: "de",
  "zh-TW": "zh",
};

/** Native / end-user labels for locale pickers; keys must stay in sync with `locales`. */
export const localeNativeLabels: Record<Locale, string> = {
  en: "English",
  de: "Deutsch",
  fr: "Français",
  ja: "日本語",
  zh: "中文（简体）",
};

/** Ordered map for nav/profile language UIs (label → locale code). Derived from `locales`. */
export const Languages: Record<string, string> = Object.fromEntries(
  locales.map((code) => [localeNativeLabels[code] ?? code, code]),
);

// Telemetry constants
export const CHANGE_LANGUAGE = "settings: change language";
export const NAVIGATE_TO_PAGE = "navigate: nav to page";
export const VIEW_CART = "shopping: view shopping car";
export const CONTINUE_SHOPPING = "shopping: continue shopping";
export const START_CHECK_OUT = "shopping: start check out";
export const SEARCH_PRODUCT = "shipping: search product";
export const SIGN_IN_WITH_EMAIL = "user: sign in with email";
export const SIGN_IN_WITH_EMAIL_FAILED = "user: sign in with email failed";
export const SIGN_UP_WITH_EMAIL = "user: sign up with email";
export const SIGN_UP_WITH_EMAIL_FAILED = "user: sign up with email failed";
export const SIGN_IN_WITH_GOOGLE = "user: sign in with google";
export const RESET_PASSWORD = "user: reset password";
export const SIGN_OUT = "user: sign out";

// Footer navigation
export const footerNavigation = {
  products: [
    { name: "gift_sets", href: "/gift-sets" },
    { name: "bracelets", href: "/products/jewellery/bracelets" },
    { name: "necklaces", href: "/products/jewellery/necklaces" },
    { name: "earrings", href: "/products/jewellery/earrings" },
    { name: "rings", href: "/products/jewellery/rings" },
  ],
  company: [
    { name: "about_us", href: "/about" },
    { name: "blog", href: "/blog" },
    // { name: "affiliate", href: "/affiliate" },
  ],
  customerService: [
    { name: "faq", href: "/customerServices" },
    { name: "contact_us", href: "/contact" },
    { name: "privacy_policy", href: "/privacy-policy" },
    { name: "terms", href: "/terms" },
  ],
};
